/**
 * AdminGate — Client-side password gate for /blog/admin.
 *
 * SECURITY NOTE: This is a *client-side* gate. The password hash is shipped
 * in the JS bundle, so anyone who downloads the bundle can attempt to
 * brute-force it offline. Use a long, random passphrase (20+ chars) and
 * treat this as obscurity, not real authentication. For real auth, enable
 * Lovable Cloud and use Supabase Auth + RLS.
 *
 * To change the password:
 *   1. Pick a new strong passphrase.
 *   2. Compute its SHA-256 (lowercase hex). Example in a browser console:
 *        const enc = new TextEncoder().encode("YOUR-PASSPHRASE");
 *        crypto.subtle.digest("SHA-256", enc).then(b =>
 *          console.log([...new Uint8Array(b)].map(x=>x.toString(16).padStart(2,"0")).join(""))
 *        );
 *   3. Replace PASSWORD_SHA256 below.
 */
import { useEffect, useState, type ReactNode } from "react";
import { Lock } from "lucide-react";
import { ButtonCustom } from "@/components/ui/button-custom";

// SHA-256 of the default password: "itqan-admin-2026" — CHANGE THIS.
const PASSWORD_SHA256 =
  "7c26d43175dcafdb647a6fa8fc4ca00c0e758f83a3cc24e29b2ca240035812a8";

const STORAGE_KEY = "itqan_admin_unlocked_v1";
const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 60_000;

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(input),
  );
  return [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

interface Props {
  children: ReactNode;
}

const AdminGate = ({ children }: Props) => {
  const [unlocked, setUnlocked] = useState(false);
  const [checked, setChecked] = useState(false);
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") setUnlocked(true);
    } catch {
      // ignore
    }
    setChecked(true);
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (lockedUntil && Date.now() < lockedUntil) {
      const s = Math.ceil((lockedUntil - Date.now()) / 1000);
      setError(`محاولات كثيرة. حاول بعد ${s} ثانية.`);
      return;
    }

    setBusy(true);
    try {
      const hash = await sha256Hex(pwd);
      if (hash === PASSWORD_SHA256) {
        try {
          sessionStorage.setItem(STORAGE_KEY, "1");
        } catch {
          // ignore
        }
        setUnlocked(true);
        setPwd("");
      } else {
        const next = attempts + 1;
        setAttempts(next);
        if (next >= MAX_ATTEMPTS) {
          setLockedUntil(Date.now() + LOCKOUT_MS);
          setAttempts(0);
          setError("تم قفل المحاولات مؤقتاً لمدة دقيقة.");
        } else {
          setError("كلمة المرور غير صحيحة.");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    setUnlocked(false);
  };

  if (!checked) return null;

  if (unlocked) {
    return (
      <>
        <button
          type="button"
          onClick={logout}
          className="fixed top-4 left-4 z-50 text-xs text-foreground/50 hover:text-sand bg-surface-2/80 backdrop-blur px-3 py-1.5 rounded-full border border-foreground/10 transition"
        >
          خروج
        </button>
        {children}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-foreground/10 bg-surface-2/60 p-8 space-y-5"
        autoComplete="off"
      >
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-sand/10 border border-sand/30 flex items-center justify-center">
            <Lock className="w-5 h-5 text-sand" />
          </div>
          <h1 className="font-display text-xl text-foreground">
            منطقة محمية
          </h1>
          <p className="text-sm text-foreground/60">
            أدخل كلمة المرور للوصول إلى محرر المقالات.
          </p>
        </div>

        <input
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="••••••••"
          dir="ltr"
          autoFocus
          disabled={busy}
          className="w-full rounded-lg bg-surface border border-foreground/10 px-4 py-2.5 text-sm text-foreground placeholder:text-foreground/30 focus:outline-none focus:border-sand/60 focus:ring-2 focus:ring-sand/20 transition text-center tracking-widest"
        />

        {error && (
          <p className="text-xs text-red-400 text-center" role="alert">
            {error}
          </p>
        )}

        <ButtonCustom
          type="submit"
          variant="foil"
          className="w-full"
          disabled={busy || !pwd}
        >
          {busy ? "جاري التحقق..." : "دخول"}
        </ButtonCustom>
      </form>
    </div>
  );
};

export default AdminGate;
